// FNP-1500: the drawer-facing hook for the derived-name availability check.
//
// Debounced, because it runs as the user types and each run is four listings.

import {
    queryOptions,
    useQuery,
} from '@tanstack/react-query';
import {useMemo} from 'react';

import {checkNameAvailability} from '../services/NameAvailabilityService';
import {
    NameAvailabilityResult,
    TargetStage,
} from '../types';
import {validateBaseName} from '../utils/deriveNames';
import {useDebouncedValue} from './useDebouncedValue';

const DEBOUNCE_MS = 400;

// Names change only when someone provisions or tears down, neither of which
// this app can miss for long. Short enough that a name freed in another tab is
// picked up on the next edit; long enough that re-typing a character does not
// re-run four listings.
const STALE_TIME_MS = 30_000;

export const createNameAvailabilityQueryOptions = (
    baseName: string,
    targetStages: TargetStage[],
) => queryOptions({
    // Every variable that changes the result is in the key. The stages are
    // flattened so the key stays a stable primitive array across renders.
    queryKey: [
        'checkNameAvailability',
        baseName,
        targetStages.map((s) => `${s.stage}:${s.accountId}`).join(','),
    ],
    queryFn: () => checkNameAvailability({baseName, targetStages}),
    staleTime: STALE_TIME_MS,
    // ⚠️ NO retry. A failed listing is a RESULT here, not an error — the
    // service turns it into UNKNOWN statuses the drawer must show. Retrying
    // would delay that answer while the user waits on a name they cannot
    // confirm. (The service does not throw, so this is belt-and-braces.)
    retry: false,
});

export interface UseNameAvailabilityOptions {
    // Stages being provisioned into. Omit while the user has not picked any:
    // the stack and bucket names cannot be derived without one.
    targetStages?: TargetStage[];
    // Lets the drawer hold the check off until the field has been touched.
    enabled?: boolean;
}

export const useNameAvailability = (
    baseName: string,
    options: UseNameAvailabilityOptions = {},
) => {
    const {targetStages = [], enabled = true} = options;

    const debouncedBaseName = useDebouncedValue(baseName.trim(), DEBOUNCE_MS, '');

    // Validated against the DEBOUNCED name so the message does not flicker
    // one keystroke ahead of the statuses beside it.
    const violations = useMemo(
        () => (debouncedBaseName.length > 0 ? validateBaseName(debouncedBaseName) : []),
        [debouncedBaseName],
    );

    // Skip the round-trip entirely for a name the rules already reject. The
    // service would refuse it too, but there is no reason to ask the API about
    // a name that can never be provisioned.
    const shouldQuery = enabled
        && debouncedBaseName.length > 0
        && violations.length === 0;

    const query = useQuery({
        ...createNameAvailabilityQueryOptions(debouncedBaseName, targetStages),
        enabled: shouldQuery,
    });

    // The result to render. When the base name is invalid no query ran, so this
    // is synthesised from the violations rather than left stale from the last
    // valid name the user typed.
    const result: NameAvailabilityResult | undefined = useMemo(() => {
        if (debouncedBaseName.length === 0) {
            return undefined;
        }
        if (violations.length > 0) {
            return {
                baseName: debouncedBaseName,
                violations,
                names: [],
                allFree: false,
                taken: [],
                unknown: [],
            };
        }
        return query.data;
    }, [debouncedBaseName, violations, query.data]);

    return {
        result,
        names: result?.names ?? [],
        violations,
        taken: result?.taken ?? [],
        unknown: result?.unknown ?? [],
        // ⚠️ False until a check has actually completed and proven every name
        // free. Never true while loading, and never true on an UNKNOWN.
        allFree: result?.allFree ?? false,
        // True while the user is ahead of the debounce, so the drawer can show
        // the check as pending rather than showing a stale verdict as current.
        isPending: shouldQuery && (query.isLoading || baseName.trim() !== debouncedBaseName),
        isFetching: query.isFetching,
        refetch: query.refetch,
    };
};
